import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {distinctUntilChanged, map, skipWhile, tap} from 'rxjs/operators';
import { ServicePathsSettings } from 'src/app/core/configuration/service-paths-settings';
import { ListOfDocumentElaborations } from 'src/app/core/models/files';
import { AppData } from 'src/app/data-store';

import {defaultFilterBody} from '../config/filters';
import {
  ElaborationDocumentFilterEntity,
  ElaborationDocumentFilterSezione,
  ElaborationDocumentFilterTopic} from "../models/elaboration-doc-filter";
import {ElaborationDocStatus} from "../models/elaboration-doc-status";
import {ElaborationDocumentListRequest} from "../models/elaboration-doc-list-request";
import {ElaborationDocResponse} from "../models/elaboration-doc-response";
import {ElaborationDocument} from "../models/elaboration-doc";
import {ElaborationDocumentListResponse} from "../models/elaboration-doc-list-response";
import { Entities } from '../models/elaboration-entities';

@Injectable({
  providedIn: 'root',
})
export class DocumentManagementService {
  appDataStore = AppData.getInstance();

  private _elaborationListLoaded = false;
  private _elaborationList: ElaborationDocumentListResponse = null;
  private _elaborationListBS$: BehaviorSubject<ElaborationDocumentListResponse> = new BehaviorSubject<ElaborationDocumentListResponse>(this._elaborationList);
  elaborationList$: Observable<ElaborationDocumentListResponse> = this._elaborationListBS$.asObservable().pipe(
    distinctUntilChanged(),
    tap(() => {
      if (!this._elaborationListLoaded) {
        this.getElaborationDocumentList(defaultFilterBody).subscribe();
      }
    }),
  );

  constructor(private http: HttpClient) {}

  /**
   * @POST
   * sends a list of file elaborations
   * @param elaboration
   * @returns
   */
  sendDocumentElaboration(
    elaboration: ListOfDocumentElaborations
  ): Observable<ElaborationDocResponse> {
    return this.http.post<ElaborationDocResponse>(
      ServicePathsSettings.DOMINIO +
        ServicePathsSettings.URL_SEND_DOCUMENT_ELABORATION,
      elaboration
    );
  }

  /**
   * @GET
   * fetches a list of document elaboration status
   * @returns
   */
  getStatusDocumentElaboration(): Observable<ElaborationDocStatus[]> {
    /// VALUTARE SE SPOSTARE ALL'INIT DELL'APP
    // Check whether the status list is already present in the App Data Store
    const statusElaborations = this.appDataStore.statiElaborazioneDocumenti;
    if (statusElaborations) {
      return of(statusElaborations);
    }
    // Perform HTTP get to fetch status when App data store does not have it
    return this.http
      .get<ElaborationDocStatus[]>(
        ServicePathsSettings.DOMINIO +
          ServicePathsSettings.URL_GET_ELABORATION_STATUS_LIST
      )
      .pipe(
        tap(() => {}),
        map((res) => (this.appDataStore.statiElaborazioneDocumenti = res))
      );
  }

  /**
   * @POST
   * Fetches a list of Document Elaboration
   * @param filters paging, rows, order
   * @returns
   */
  getElaborationDocumentList(
    filters: ElaborationDocumentListRequest
  ): Observable<ElaborationDocumentListResponse> {
    return this.http.post<any>(
      ServicePathsSettings.DOMINIO +
        ServicePathsSettings.URL_FETCH_DOCUMENT_ELABORATIONS,
      filters
    ).pipe(
      tap(res => {
        this._elaborationList = res;
        this._elaborationListBS$.next(this._elaborationList);
        this._elaborationListLoaded = true;
      })
    );
  }

  /**
   * @DELETE
   * Deletes an elaboration
   * @param idElaborazione
   * @returns
   */
  deleteElaboration(idElaborazione: string): Observable<{ id: string }> {
    return this.http.delete<{ id: string }>(
      ServicePathsSettings.DOMINIO +
        ServicePathsSettings.URL_ELABORATION(idElaborazione)
    );
  }

  getDocuments(idElaborazione: string): Observable<any>  {
    return this.http.get(ServicePathsSettings.DOMINIO + ServicePathsSettings.URL_GET_ELABORATION_DOCUMENTS(idElaborazione))
  }

  getElaborationDocument$(id: string): Observable<ElaborationDocument> {
    if(this._elaborationList){
      return this.elaborationList$.pipe(
        skipWhile(elaborations => !elaborations),
        map(elaborations => {
          return elaborations.elaborazioni.find(data => data.idElaborazione === id) as ElaborationDocument
        })
      );
    } else {
      return this.http.get<ElaborationDocument>(ServicePathsSettings.DOMINIO + ServicePathsSettings.URL_ELABORATION(id));
    }
  }

  getElaborationTopics(id: string): Observable<ElaborationDocumentFilterTopic[]> {
    return this.http.get<ElaborationDocumentFilterTopic[]>(ServicePathsSettings.DOMINIO + ServicePathsSettings.URL_GET_ELABORATION_TOPICS(id));
  }
  getElaborationEntitiesModal (id : string) {
    return this.http.get<Entities[]>(ServicePathsSettings.DOMINIO + ServicePathsSettings.URL_GET_ELABORATION_ENTITIES(id));
  }

  getElaborationEntities(id: string): Observable<ElaborationDocumentFilterEntity[]> {
    return this.http.get<ElaborationDocumentFilterEntity[]>(ServicePathsSettings.DOMINIO + ServicePathsSettings.URL_GET_ELABORATION_ENTITIES(id));
  }

  getElaborationSimilarDocuments (id : string ) {
    return this.http.get(ServicePathsSettings.DOMINIO + ServicePathsSettings.URL_GET_ELABORATION_SIMILAR_DOCUMENTS(id))
  }
  getElaborationCitationsDocuments (id : string ) {
    return this.http.get(ServicePathsSettings.DOMINIO + ServicePathsSettings.URL_GET_ELABORATION_CITATIONS(id))
  }

  getElaborationSections(id: string): Observable<ElaborationDocumentFilterSezione[]> {
    return this.http.get<ElaborationDocumentFilterSezione[]>(ServicePathsSettings.DOMINIO + ServicePathsSettings.URL_GET_ELABORATION_SEZIONI(id));
  }



  getDocumentToProcess(): Observable<any> {
    return this.http.get(ServicePathsSettings.DOMINIO + ServicePathsSettings.URL_ELABORATION_TEMP)
  }


  getPraticheByAnnoRuolo(anno: string, ruolo: string): Observable<any[]>{
    return this.http
    .get<any>(ServicePathsSettings.DOMINIO + ServicePathsSettings.URL_GET_ELABORATION_PRATICHE_ANNO_RUOLO(anno,ruolo))
    .pipe(
      tap(res => console.log(res)),
      map(it => it.pratiche));
  }

  getElaborationPratiche(idPratica: string): Observable<any[]> {
    return this.http
      .get<any>(ServicePathsSettings.DOMINIO + ServicePathsSettings.URL_GET_ELABORATION_PRATICHE(idPratica))
      .pipe(
        tap(res => console.log(res)),
        map(it => it.pratiche));
  //     .pipe(map(() => {
  //       return [
  //         {
  //           "id": "c5e3fafe-db17-4eae-a9f9-72ca44f7dc64",
  //           "tipoPratica": {
  //             "id": "58a00511-53e7-11ea-a08d-005056a8dd09",
  //             "descrizione": "CIVILE",
  //             "label": "CIVILE",
  //             "rimosso": null,
  //             "endpoint": null,
  //             "tipiFascicolo": []
  //           },
  //           "stato": {
  //             "id": "BOZZA",
  //             "descrizione": "Bozza",
  //             "label": null
  //           },
  //           "dataDefinizione": null,
  //           "motivoDefinizione": null,
  //           "definito": false,
  //           "dataArchiviazione": null,
  //           "archiviata": false,
  //           "capofila": false,
  //           "riunita": false,
  //           "correlata": false,
  //           "conflittoDiInteressi": null,
  //           "posizione": null,
  //           "vecchiaPosizione": null,
  //           "note": null,
  //           "dataApertura": null,
  //           "soggetto": null,
  //           "recuperoCrediti": false,
  //           "oggetto": null,
  //           "intestazione": {
  //             "campi": {
  //               "idTipoPratica": "58a00511-53e7-11ea-a08d-005056a8dd09",
  //               "tipoPratica": "CIVILE",
  //               "dataDefinizione": null,
  //               "dataArchiviazione": null,
  //               "contropartePrimaria": "Mario Rossi",
  //               "oggetto": "RUOLI",
  //               "societa": null,
  //               "idStatoPratica": "BOZZA",
  //               "statoPratica": "Bozza",
  //               "posizionePratica": "2023/98",
  //               "definita": "true",
  //               "materia": "PREVIDENZA",
  //               "dataAperturaPratica": 1682513542000
  //             }
  //           },
  //           "fascicoliLight": [
  //             {
  //               "id": "69b2a03e-e0bd-4d5c-a40f-b94560225e18",
  //               "idParent": "c5e3fafe-db17-4eae-a9f9-72ca44f7dc64",
  //               "tipoFascicolo": {
  //                 "id": "3426c0a8-53e9-11ea-a08d-005056a8dd09",
  //                 "descrizione": null,
  //                 "label": null,
  //                 "rimosso": null,
  //                 "endpoint": null,
  //                 "categoria": null,
  //                 "tipiSezione": []
  //               },
  //               "stato": {
  //                 "id": "IN_SMISTAMENTO",
  //                 "descrizione": "In smistamento",
  //                 "label": null
  //               },
  //               "dataDefinizione": null,
  //               "dataNotifica": null,
  //               "motivoDefinizione": null,
  //               "definito": false,
  //               "grado": null,
  //               "posizione": null,
  //               "vecchiaPosizione": null,
  //               "flagProcessoTelematico": null,
  //               "flagFondoRischi": null,
  //               "flagValorizzazione": null,
  //               "dataVariazioneFondoRischi": null,
  //               "natura": null,
  //               "dataInizioFascicolo": null,
  //               "note": null,
  //               "rifiuto": null,
  //               "visible": true,
  //               "registroFascicolo": null,
  //               "numeroProtocollo": null,
  //               "dataProtocollazione": null,
  //               "recuperoCrediti": null,
  //               "intestazione": {
  //                 "campi": {
  //                   "grado": null,
  //                   "riunito": false,
  //                   "tipoFascicolo": "ORDINARIO",
  //                   "dataDefinizione": null,
  //                   "contropartePrimaria": "Mario Rossi",
  //                   "definito": false,
  //                   "capofila": false,
  //                   "dataInizioFascicolo": 1682513542000,
  //                   "statoFascicolo": "In smistamento",
  //                   "posizione": "2023/98-1",
  //                   "idStatoFascicolo": "IN_SMISTAMENTO",
  //                   "id": "69b2a03e-e0bd-4d5c-a40f-b94560225e18",
  //                   "idParent": "c5e3fafe-db17-4eae-a9f9-72ca44f7dc64"
  //                 }
  //               },
  //               "riassunto": null,
  //               "campi-dinamici": []
  //             }
  //           ],
  //           "classificazioni": [
  //             {
  //               "id": "cf8ac49b-b527-402d-afa5-6e030a447b8e",
  //               "materia": {
  //                 "id": "b45ec012-4f18-11ea-a08d-005056a8dd09",
  //                 "codice": null,
  //                 "descrizione": null,
  //                 "label": null,
  //                 "rimosso": null,
  //                 "sigla": null
  //               },
  //               "oggetto": {
  //                 "id": "c14cb087-c7a4-4bc4-a0b0-4ba3c09490f7",
  //                 "codice": null,
  //                 "descrizione": null,
  //                 "label": null,
  //                 "rimosso": null
  //               },
  //               "argomento": {
  //                 "id": null,
  //                 "codice": null,
  //                 "descrizione": null,
  //                 "label": null,
  //                 "rimosso": null
  //               },
  //               "isPrincipale": null
  //             }
  //           ],
  //           "visible": null,
  //           "campi-dinamici": []
  //         }
  //       ]
  //     }))
   }


}
